"use client";

import { useCallback, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useUniversalAuth } from "@wtc/ui";
import type { Message } from "@/lib/core/crm/types";
import { useGoogleDriveStatus } from "@/lib/hooks/google-drive/use-google-drive-status";
import {
  useGoogleDriveFiles,
  useCreateGoogleDriveFolder,
} from "@/lib/hooks/google-drive/use-google-drive-browser";
import { useMessageAttachments } from "@/lib/hooks/messages/useMessageAttachments";
import {
  CUSTOMER_ROOT_FOLDER_ID,
  type Attachment,
  type ClientFolder,
  type UploadResults,
} from "./SaveAttachmentsToGoogleDriveModal.types";

interface UseSaveAttachmentsOptions {
  message: Message;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function useSaveAttachmentsToGoogleDriveState({
  message,
  onOpenChange,
  open,
}: UseSaveAttachmentsOptions) {
  const { session } = useUniversalAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState<ClientFolder | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());
  const [showCreateFolderForm, setShowCreateFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const createFolderMutation = useCreateGoogleDriveFolder();

  const { data: status, isLoading: statusLoading } = useGoogleDriveStatus({ enabled: open });
  const hasAnyDriveAccess =
    !!status?.primary?.hasDriveAccess || !!status?.secondary?.hasDriveAccess;
  const {
    data: attachments = [],
    isLoading: attachmentsLoading,
    error: attachmentsError,
  } = useMessageAttachments(message, open);
  const {
    data: rootListing,
    isLoading: listingLoading,
    refetch: refetchFolders,
  } = useGoogleDriveFiles({
    enabled: hasAnyDriveAccess && open,
    parentId: CUSTOMER_ROOT_FOLDER_ID,
    pageSize: 1000,
  });

  const driveIdFromRoot = useMemo(() => {
    const anyItem =
      rootListing?.files && rootListing.files.length > 0 ? rootListing.files[0] : undefined;
    return anyItem?.driveId;
  }, [rootListing]);

  const clientFolders = useMemo(() => {
    const folders = (rootListing?.files || [])
      .filter((f) => f.isFolder)
      .map((f) => ({
        id: f.id,
        name: f.name,
        driveId: f.driveId,
      }));

    if (!searchTerm.trim()) return folders;

    const lowerSearch = searchTerm.toLowerCase();
    return folders.filter((f) => f.name.toLowerCase().includes(lowerSearch));
  }, [rootListing, searchTerm]);

  const handleUpload = useCallback(async () => {
    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }
    if (!attachments || attachments.length === 0) {
      toast.error("No attachments found in this message");
      return;
    }
    if (selectedAttachmentIds.size === 0) {
      toast.error("Please select at least one attachment to upload");
      return;
    }
    if (!session?.access_token) {
      toast.error("No authentication token found");
      return;
    }

    setIsUploading(true);
    setUploadResults(null);

    try {
      const response = await fetch("/api/messages/download-and-upload-attachments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          provider: message.provider,
          messageId: message.providerMessageId,
          attachmentIds: Array.from(selectedAttachmentIds),
          parentId: selectedClient.id,
          driveId: selectedClient.driveId,
        }),
      });

      if (!response.ok) {
        let errorMessage = "Failed to upload attachments";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Upload failed: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setUploadResults({
        uploaded: result.uploadedFiles || [],
        errors: result.errors || [],
      });

      if (result.uploadedFiles && result.uploadedFiles.length > 0) {
        toast.success(
          `Successfully uploaded ${result.uploadedFiles.length} attachment(s) to "${selectedClient.name}"`
        );
      }
      if (result.errors && result.errors.length > 0) {
        toast.error(`Failed to upload ${result.errors.length} attachment(s). See details below.`);
      }
    } catch (error: unknown) {
      console.error("Error uploading attachments:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to upload attachments: ${errorMessage}`);
      setUploadResults({
        uploaded: [],
        errors: [{ filename: "Upload", error: errorMessage }],
      });
    } finally {
      setIsUploading(false);
    }
  }, [selectedClient, attachments, selectedAttachmentIds, session, message]);

  const handleClose = useCallback(() => {
    setSelectedClient(null);
    setSearchTerm("");
    setUploadResults(null);
    setSelectedAttachmentIds(new Set());
    setIsUploading(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const toggleAttachmentSelection = useCallback((attachmentId: string) => {
    setSelectedAttachmentIds((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(attachmentId)) {
        newSelected.delete(attachmentId);
      } else {
        newSelected.add(attachmentId);
      }
      return newSelected;
    });
  }, []);

  const selectAllAttachments = useCallback(() => {
    if (attachments && attachments.length > 0) {
      setSelectedAttachmentIds(new Set(attachments.map((att: Attachment) => att.id)));
    }
  }, [attachments]);

  const deselectAllAttachments = useCallback(() => {
    setSelectedAttachmentIds(new Set());
  }, []);

  const handleCreateFolderSuccess = useCallback(
    (newFolder: { id: string; name: string; driveId?: string }) => {
      setSelectedClient({
        id: newFolder.id,
        name: newFolder.name,
        driveId: newFolder.driveId,
      });
      setShowCreateFolderForm(false);
      setNewFolderName("");
      toast.success(`Folder "${newFolder.name}" created and selected`);

      console.log("Refetching folders after folder creation...");
      refetchFolders()
        .then(() => {
          console.log("Folders refetched successfully");
        })
        .catch((error) => {
          console.error("Error refetching folders:", error);
        });
    },
    [refetchFolders]
  );

  const handleCreateFolderSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!newFolderName.trim()) {
        toast.error("Please enter a folder name");
        return;
      }

      try {
        const result = await createFolderMutation.mutateAsync({
          folderName: newFolderName.trim(),
          parentId: CUSTOMER_ROOT_FOLDER_ID,
          driveId: driveIdFromRoot,
          isSecondAccount: false,
        });
        handleCreateFolderSuccess(result.folder);
      } catch (error: unknown) {
        console.error("Error creating folder:", error);
        const message = error instanceof Error ? error.message : "Unknown error";
        toast.error(`Failed to create folder: ${message}`);
      }
    },
    [createFolderMutation, driveIdFromRoot, handleCreateFolderSuccess, newFolderName]
  );

  const handleRefreshFolders = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchFolders();
      toast.success("Folder list refreshed");
    } catch (error) {
      console.error("Error refreshing folders:", error);
      toast.error("Failed to refresh folder list");
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchFolders]);

  return {
    attachments,
    attachmentsError,
    clientFolders,
    createFolderMutation,
    deselectAllAttachments,
    handleClose,
    handleCreateFolderSubmit,
    handleRefreshFolders,
    handleUpload,
    hasAnyDriveAccess,
    isLoading: statusLoading || listingLoading || attachmentsLoading,
    isRefreshing,
    isUploading,
    listingLoading,
    newFolderName,
    searchTerm,
    selectAllAttachments,
    selectedAttachmentIds,
    selectedClient,
    setNewFolderName,
    setSearchTerm,
    setSelectedClient,
    setShowCreateFolderForm,
    showCreateFolderForm,
    statusLoading,
    toggleAttachmentSelection,
    uploadResults,
  };
}
